/** Formatação em pt-BR e conversões seguras dos valores monetários da API. */

import type { Dinheiro, FormaPagamento } from '../types';

const MOEDA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const MOEDA_INTEIRA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Converte o `Decimal` serializado da API para número (use só para exibir). */
export function paraNumero(valor: Dinheiro | number | null | undefined): number {
  if (valor === null || valor === undefined || valor === '') return 0;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/** R$ 1.234,56 */
export function moeda(valor: Dinheiro | number | null | undefined): string {
  return MOEDA.format(paraNumero(valor));
}

/** R$ 1.235 — para KPIs, onde os centavos só poluem. */
export function moedaCurta(valor: Dinheiro | number | null | undefined): string {
  return MOEDA_INTEIRA.format(paraNumero(valor));
}

/** Valor absoluto formatado, com o sinal decidido por quem chama. */
export function moedaAbs(valor: Dinheiro | number | null | undefined): string {
  return MOEDA.format(Math.abs(paraNumero(valor)));
}

/** Cor do saldo do irmão: positivo é dívida, zero e crédito são verdes.
 *
 * Zero é verde, e sai como `R$ 0,00` — nunca como travessão. O tracinho parece
 * cadastro sem dado, e quem está quite tem de aparecer quite: na tela de Irmãos
 * os sete irmãos sem competência lançada eram lidos como "sem saldo".
 */
export function corDoSaldo(saldo: number): string {
  return saldo > 0 ? 'var(--rd)' : 'var(--gn)';
}

export function moedaCurtaAbs(valor: Dinheiro | number | null | undefined): string {
  return MOEDA_INTEIRA.format(Math.abs(paraNumero(valor)));
}

/** Hoje em ISO, montado a partir do fuso local — `toISOString` devolveria UTC
 *  e viraria o dia errado à noite, no Brasil. */
export function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** '2026-06-17' -> '17/06/2026' (sem passar por Date, que desloca fuso). */
export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

const MESES_CURTOS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export const MESES_LONGOS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** '2026-06-01' -> 'Jun/26' */
export function competencia(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes] = iso.slice(0, 10).split('-');
  return `${MESES_CURTOS[Number(mes) - 1]}/${ano.slice(2)}`;
}

export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Iniciais para o avatar: 'Décio Moritz Júnior' -> 'DM' */
export function iniciais(nome: string): string {
  return nome
    .split(/\s+/)
    .filter((p) => p.length > 2)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function porcentagem(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '—' : `${Math.round(valor)}%`;
}

/** Formas de pagamento na ordem em que a tesouraria as usa. */
export const FORMAS_PAGAMENTO: { valor: FormaPagamento; rotulo: string }[] = [
  { valor: 'pix', rotulo: 'PIX' },
  { valor: 'boleto', rotulo: 'Boleto' },
  { valor: 'transferencia', rotulo: 'Transferência' },
  { valor: 'debito', rotulo: 'Débito' },
  { valor: 'credito', rotulo: 'Cartão de crédito' },
  { valor: 'debito_automatico', rotulo: 'Débito automático' },
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'outro', rotulo: 'Outro' },
];

const ROTULOS_SITUACAO: Record<string, string> = {
  adiantado: 'Adiantado',
  em_dia: 'Em dia',
  atencao: 'Atenção',
  inadimplente: 'Inadimplente',
  acordo: 'Em acordo',
  licenca_medica: 'Licença médica',
  isento: 'Isento',
};

export const ROTULOS_CONDICAO: Record<string, string> = {
  normal: 'Normal',
  emerito: 'Emérito',
  remido_idade: 'Remido por idade',
  remido_invalidez: 'Remido por invalidez',
};

export const ROTULOS_BENEFICIO: Record<string, string> = {
  nenhum: 'Sem benefício',
  desconto_percentual: 'Desconto percentual',
  desconto_valor: 'Desconto em reais',
  isencao: 'Isenção total',
};

export function rotuloSituacao(situacao: string): string {
  return ROTULOS_SITUACAO[situacao] ?? situacao;
}

const ROTULOS_TIPO_SESSAO: Record<string, string> = {
  ordinaria: 'Ordinária',
  administrativa: 'Administrativa',
  magna: 'Magna',
  mesa: 'Loja de Mesa',
  financas: 'Finanças',
  comentada: 'Comentada',
  eleitoral: 'Eleitoral',
  especial: 'Especial',
};

export function rotuloTipoSessao(tipo: string): string {
  return ROTULOS_TIPO_SESSAO[tipo] ?? tipo;
}

const ROTULOS_DOCUMENTO: Record<string, string> = {
  nfe: 'NF-e',
  nfse: 'NFS-e',
  recibo: 'Recibo',
  boleto: 'Boleto',
  comprovante: 'Comprovante',
  sem_documento: 'Sem NF',
};

export function rotuloDocumento(tipo: string): string {
  return ROTULOS_DOCUMENTO[tipo] ?? tipo;
}

// Glifos geométricos (não emoji): herdam a cor do texto e têm o mesmo desenho em
// qualquer sistema.
/** Classe de cor do chip: o tipo de sessão manda quando é conhecido; o
 *  genérico (evento, feriado) atende o que não é sessão de loja. */
export function classeDoTipo(item: {
  tipo: 'sessao' | 'evento' | 'feriado';
  tipo_sessao: string | null;
}): string {
  return item.tipo_sessao ? `ts-${item.tipo_sessao}` : `ev-${item.tipo}`;
}

/** O que o chip escreve: a natureza da sessão, ou o genérico. */
export function rotuloDoTipo(item: {
  tipo: 'sessao' | 'evento' | 'feriado';
  tipo_sessao: string | null;
}): string {
  if (item.tipo_sessao) return rotuloTipoSessao(item.tipo_sessao);
  return item.tipo === 'feriado' ? 'Feriado' : item.tipo === 'evento' ? 'Evento' : 'Sessão';
}

export const GRAU_ICONE: Record<number, string> = { 1: '▲', 2: '◆', 3: '■' };
export const GRAU_NOME: Record<number, string> = {
  1: 'Aprendiz',
  2: 'Companheiro',
  3: 'Mestre',
};

/** Remove acentos e caixa — usado nos filtros de busca do cliente. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // marcas de acentuação combinantes
    .toLowerCase()
    .trim();
}
